import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { CameraIcon, IconText, InfoIcon } from '@motorway/mw-highway-code';

import { DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsDocumentCapture';

import { Input } from 'Containers/app/documentCapture/uploadDocuments/pages/uploadImageReducer.types';

import { DOCUMENT_ERROR_TRIGGER, DOCUMENT_KINDS, generateUploadDocumentAnalyticsEventString } from '../../../containers/app/documentCapture/DocumentCapture.helpers';
import { useImageManager } from '../../../containers/app/documentCapture/uploadDocuments/hooks/useImageManager';
import UploadDocumentsTexts from '../../../containers/app/documentCapture/uploadDocuments/UploadDocuments.texts.json';
import UploadDocumentsTextsDrftTexts from '../../../containers/app/documentCapture/uploadDocuments/UploadDocumentsDRFT.texts.json';
import { ReviewStatus } from '../../../containers/app/documentCapture/uploadDocuments/UploadDocumentsSection/UploadDocumentsSection.types';
import { UploadDocumentStateLabel } from '../../../containers/app/documentCapture/uploadDocuments/UploadDocumentsSection/UploadDocumentStateLabel';

import drftStyles from './DocumentImageOverlayDrft.module.scss';
import styles from './DocumentImagePreview.module.scss';

interface FilePlaceholderProps {
  handleOnChange: React.ChangeEventHandler<HTMLInputElement>;
  handleOnClick: () => void;
  id: string;
  isV5: boolean;
  isVisible: boolean;
  pageNumber: number;
  ref: React.RefObject<HTMLInputElement | null>;
  showSpinner: boolean;
}
const UploadDocumentsT = new MDText(UploadDocumentsTexts);
const UploadDocumentsTextsDRFT = new MDText(UploadDocumentsTextsDrftTexts);

const FilePlaceholder = (
  {
    handleOnChange,
    handleOnClick,
    id,
    isV5,
    isVisible,
    pageNumber,
    ref: inputRef,
    showSpinner,
  }: FilePlaceholderProps,
) => {
  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement> | React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.keyCode === 13 /* Enter key */) {
      handleOnClick();
    }
  };

  return (
    <button
      className={drftStyles.button}
      data-testid="upload-document-image-preview-drop-zone"
      id={id}
      onClick={handleOnClick}
      onKeyDown={handleOnKeyDown}
      tabIndex={!isVisible ? -1 : undefined}
    >
      {!showSpinner && (<div className={drftStyles.content}>
        <div className={drftStyles.icon}>
          <CameraIcon />
        </div>
        <h6 className={drftStyles.label}>
          {isV5 ? UploadDocumentsTextsDRFT.translate('placeHolder.addPage', { pageNumber }) : UploadDocumentsTextsDRFT.translate('placeHolder.addPhoto') }
        </h6>
      </div>)
      }
      <input
        ref={inputRef}
        accept="image/*,.heic,.pdf"
        onChange={handleOnChange}
        style={{ display: 'none' }}
        type="file"
      />
    </button>
  );
};

interface DocumentImagePreviewProps {
  id: string;
  image: Input;
  isBlurred: boolean;
  loading?: boolean;
  onChange: (file: File) => void;
  onClick: (openFileViewer: () => void) => void;
  onDelete: (documentId: number) => void;
  previewImage?: string;
  reviewStatus: ReviewStatus;
}

export const DocumentImagePreview = ({
  id,
  image,
  isBlurred,
  loading = false,
  onChange,
  onClick,
  onDelete,
  previewImage,
  reviewStatus,
}: DocumentImagePreviewProps) => {
  const [isImageLoading, setIsImageLoading] = useState(false);

  const inputFile = useRef<HTMLInputElement>(null);
  const isComplete = reviewStatus === ReviewStatus.passed;
  const needsAction = reviewStatus === ReviewStatus.actionRequired;

  const {
    hasRequestedReuploadActions,
    kind,
    showErrorBox,
    uploadDocType,
  } = useImageManager();

  const url = image?.url;

  let reuploadReason: string = 'Reason not supplied';
  const hasReason = image?.actions?.needsReupload?.reason;

  if (!image.isSharp && !hasReason) {
    reuploadReason = 'Image is not clear';
  } else if (hasReason) {
    reuploadReason = hasReason;
  }

  useEffect(() => {
    if (url) {
      setIsImageLoading(true);
    } else {
      setIsImageLoading(false);
    }
  }, [url]);

  const showSpinner = loading || isImageLoading;
  const renderImage = Boolean(url) && !loading;
  const showOverlay = (needsAction || isComplete) && !showSpinner;

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (inputFile.current?.files) {
      onChange(inputFile.current.files[0]);
      event.target.value = null!;
    }
  };

  const handleOnClick = () => {
    // Note: This triggers `handleOnPlaceHolderClick`
    const openFileViewer = () => inputFile?.current?.click();

    onClick(openFileViewer);

    if (needsAction) {
      const message = `${kind} (${reuploadReason})`;

      DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.DOCUMENTS_UPLOAD_OPENED_ON_TAP_TO_RETRY_CLICKED(
        generateUploadDocumentAnalyticsEventString({ hasRequestedReuploadActions, message, uploadDocType }),
      );
    }
  };

  // Created a new function specifically to handle `handleOnClick` being called twice
  // When an overlay (Retry) is clicked, this calls `handleOnClick` which then calls `inputFile?.current?.click();`
  // This in turn calls the onClick on `FilePlaceholder` which also called `handleOnClick`.
  // To prevent analytic events being called twice, we can gate `handleOnClick` here
  const handleOnPlaceHolderClick = () => {
    if (!needsAction) {
      handleOnClick();
    }
  };

  const handleDelete = () => {
    onDelete(image.documentId!);
  };

  const handleImageLoaded = () => setIsImageLoading(false);

  const isV5 = kind === DOCUMENT_KINDS.V5;
  const isDrivingLicence = kind === DOCUMENT_KINDS.DRIVING_LICENCE;
  const pageNumberAndLabel = isDrivingLicence
    ? UploadDocumentsT.translate('loadingContainer.ifDrivingLicence', { imageLabel: `${image.label}` })
    : UploadDocumentsT.translate('loadingContainer.page', { pageNumber: `${image.pageNumber}` });

  const showDocumentCaptureError = showErrorBox === DOCUMENT_ERROR_TRIGGER.ON_SUBMISSION && !needsAction && !image.url;

  return (
    <>
      {showDocumentCaptureError && <div className={drftStyles.drftTooltipIcon}><IconText icon={InfoIcon} text={UploadDocumentsT.translate('noDocumentsUploaded.tooltip')} variant='xsmall--small'></IconText></div>}
      <div className={cx(styles.photoBox, drftStyles.uploadThumbnail, {
        [styles.populated]: showOverlay,
        [styles.complete]: isComplete,
        [drftStyles.uploadThumbnailError]: showDocumentCaptureError,
      })} data-cy='photoBox'>
        {renderImage ? (
          <div className={styles.overlay}>
            <img
              className={cx(styles.image, {
                [styles.landscapeImage]: isDrivingLicence,
              })}
              onLoad={handleImageLoaded} src={url}
            />
          </div>
        ) : null}
        <FilePlaceholder
          ref={inputFile}
          handleOnChange={handleOnChange}
          handleOnClick={handleOnPlaceHolderClick}
          id={id}
          isV5={isV5}
          isVisible={!renderImage}
          pageNumber={image.pageNumber}
          showSpinner={showSpinner}
        />
        {(showSpinner)
          ? (
            <>
              <div className={styles.loadingContainer} data-cy='loadingSpinner' data-testid="drft-loading-spinner" />
              <div className={cx(styles.previewImage, {
                [styles.landscapeImage]: isDrivingLicence,
              })}>
                {previewImage && <img alt="document preview" src={previewImage} />}
              </div>
            </>
          )
          : null}
        {(image.url || previewImage) ? (
          <UploadDocumentStateLabel
            handleDelete={handleDelete}
            handleOnClick={handleOnClick}
            hasImage={Boolean(image.url)}
            invalidReason={image?.actions?.needsReupload?.reason}
            isBlurred={isBlurred}
            isImageLoading={showSpinner}
            needsAction={needsAction}
            pageNumberAndLabel={pageNumberAndLabel}
            reviewStatus={reviewStatus}
          />
        ) : null}
      </div>
    </>
  );
};
